/**
 * MOMENTUM_OVERLAY — Power Trend Momentum Overlay
 *
 * Draws directly on the candle pane (isStack=true, paneId='candle_pane').
 *
 * Visual layers (draw order):
 *   1. Green background fill on every bar where the power trend is ACTIVE
 *   2. EMA21 (solid blue) + SMA50 (dashed amber) polylines
 *   3. Signal vertical: solid green line on the trigger bar
 *      End verticals: dashed red (10% drawdown) or dashed amber (EMA21 < SMA50)
 *   4. Stop level: horizontal dashed amber line + "SL xxx.xx" right-edge label
 *   5. Status badge: "⬤ POWER TREND" (green) or "○ WATCHING" (gray) top-right
 *
 * Power Trend rules:
 *   C1 – Index low above EMA21 for ≥10 consecutive bars
 *   C2 – EMA21 above SMA50 for ≥5 consecutive bars
 *   C3 – SMA50 is actively uptrending (today's SMA50 > yesterday's SMA50)
 *   C4 – Strong (green) close: close > open
 *   Signal fires when C1+C2+C3 are already met and C4 occurs.
 *   Exits: close falls >10% below peak close since signal, OR EMA21 < SMA50.
 */

import { IndicatorSeries, type IndicatorTemplate } from 'klinecharts';

// ── Types ─────────────────────────────────────────────────────────────────────

export type MomentumResult = {
  ema21:           number | null;
  sma50:           number | null;
  c1_streak:       number;
  c2_streak:       number;
  c1_met:          boolean;
  c2_met:          boolean;
  c3_met:          boolean;
  c4_green:        boolean;
  cond_count:      number;        // 0–3: how many of C1/C2/C3 are currently met
  in_trend:        boolean;
  is_signal:       boolean;
  is_end_drawdown: boolean;
  is_end_cross:    boolean;
  stop_level:      number | null;
};

export interface MomentumState {
  results:   MomentumResult[];
  inTrend:   boolean;
  c1Streak:  number;
  c2Streak:  number;
  c1Met:     boolean;
  c2Met:     boolean;
  c3Met:     boolean;
  c4Green:   boolean;
  stopLevel: number | null;
}

declare global {
  interface Window { __momentum_state?: MomentumState; }
}

// ── Constants ─────────────────────────────────────────────────────────────────

const C1_THRESHOLD = 10;   // consecutive bars: low >= EMA21
const C2_THRESHOLD = 5;    // consecutive bars: EMA21 > SMA50
const EMA_PERIOD   = 21;
const SMA_PERIOD   = 50;
const STOP_PCT     = 0.10; // 10% drawdown from peak close
const EMA_ALPHA    = 2 / (EMA_PERIOD + 1);

// Draw colors
const EMA21_COLOR     = '#3B82F6';              // blue
const SMA50_COLOR     = '#F59E0B';              // amber
const FILL_COLOR      = 'rgba(34,197,94,0.07)'; // light green fill
const SIGNAL_COLOR    = 'rgba(34,197,94,0.85)'; // solid green vertical
const END_DROP_COLOR  = 'rgba(239,83,80,0.80)'; // red dashed vertical
const END_CROSS_COLOR = 'rgba(251,146,60,0.80)'; // amber dashed vertical
const STOP_LINE_COLOR = '#F59E0B';
const BADGE_ACTIVE    = 'rgba(34,197,94,0.95)';

// ── Shared compute helper (also used by momentumTracker.ts) ───────────────────

type KBar = { open: number; high: number; low: number; close: number };

export function computeMomentum(bars: KBar[]): MomentumResult[] {
  const n = bars.length;
  const results: MomentumResult[] = new Array(n);

  let ema21: number | null = null;
  let sma50Sum = 0;
  let c1_streak = 0;
  let c2_streak = 0;
  let in_trend  = false;
  let peak_close: number | null = null;
  let prevSma50: number | null = null;

  for (let i = 0; i < n; i++) {
    const bar   = bars[i];
    const close = bar.close;
    const open  = bar.open;
    const low   = bar.low;

    // ── EMA21 ──────────────────────────────────────────────────────────────
    ema21 = ema21 === null ? close : close * EMA_ALPHA + ema21 * (1 - EMA_ALPHA);

    // ── SMA50 rolling sum ──────────────────────────────────────────────────
    sma50Sum += close;
    if (i >= SMA_PERIOD) sma50Sum -= bars[i - SMA_PERIOD].close;
    const sma50: number | null = i >= SMA_PERIOD - 1 ? sma50Sum / SMA_PERIOD : null;

    // ── C1: low >= EMA21 consecutive streak ────────────────────────────────
    if (low >= ema21) c1_streak++; else c1_streak = 0;

    // ── C2: EMA21 > SMA50 consecutive streak ───────────────────────────────
    if (sma50 !== null && ema21 > sma50) c2_streak++; else c2_streak = 0;

    // ── C3: SMA50 sloping up ───────────────────────────────────────────────
    const c3_met = sma50 !== null && prevSma50 !== null && sma50 > prevSma50;

    // ── C4: green candle ───────────────────────────────────────────────────
    const c4_green = close > open;

    const c1_met = c1_streak >= C1_THRESHOLD;
    const c2_met = c2_streak >= C2_THRESHOLD;
    const cond_count = (c1_met ? 1 : 0) + (c2_met ? 1 : 0) + (c3_met ? 1 : 0);

    // ── State machine ──────────────────────────────────────────────────────
    let is_signal       = false;
    let is_end_drawdown = false;
    let is_end_cross    = false;
    let stop_level: number | null = null;

    if (in_trend) {
      // Update peak close
      if (peak_close === null || close > peak_close) peak_close = close;
      stop_level = peak_close * (1 - STOP_PCT);

      if (close < stop_level) {
        // Exit A: close fell >10% below peak close
        is_end_drawdown = true;
        in_trend        = false;
        peak_close      = null;
      } else if (sma50 !== null && ema21 < sma50) {
        // Exit B: EMA21 crossed below SMA50
        is_end_cross = true;
        in_trend     = false;
        peak_close   = null;
      }
    } else {
      // Entry: all four conditions satisfied
      if (c1_met && c2_met && c3_met && c4_green) {
        is_signal  = true;
        in_trend   = true;
        peak_close = close;
        stop_level = close * (1 - STOP_PCT);
      }
    }

    results[i] = {
      ema21, sma50,
      c1_streak, c2_streak,
      c1_met, c2_met, c3_met, c4_green,
      cond_count,
      in_trend, is_signal, is_end_drawdown, is_end_cross,
      stop_level,
    };

    prevSma50 = sma50;
  }

  return results;
}

// ── Internal draw-param type ──────────────────────────────────────────────────

type DrawParams = {
  ctx:          CanvasRenderingContext2D;
  kLineDataList: { open: number; high: number; low: number; close: number }[];
  visibleRange: { from: number; to: number };
  bounding:     { left: number; top: number; right: number; bottom: number; width: number; height: number };
  xAxis:        { convertToPixel: (i: number) => number };
  yAxis:        { convertToPixel: (v: number) => number };
  indicator:    { result: MomentumResult[] };
  barSpace:     { bar: number; gapBar: number; halfGapBar: number };
};

// ── Indicator template ────────────────────────────────────────────────────────

const momentumOverlay: IndicatorTemplate<MomentumResult> = {
  name:       'MOMENTUM_OVERLAY',
  shortName:  'Momentum',
  series:     IndicatorSeries.Normal,
  shouldOhlc: false,
  figures:    [],    // all rendering is manual

  // ── calc ────────────────────────────────────────────────────────────────
  calc: (dataList) => {
    const bars = dataList as unknown as KBar[];
    const results = computeMomentum(bars);

    // Expose last-bar snapshot for MomentumConditionCards
    const last = results[results.length - 1];
    if (last) {
      window.__momentum_state = {
        results,
        inTrend:  last.in_trend,
        c1Streak: last.c1_streak,
        c2Streak: last.c2_streak,
        c1Met:    last.c1_met,
        c2Met:    last.c2_met,
        c3Met:    last.c3_met,
        c4Green:  last.c4_green,
        stopLevel: last.stop_level,
      };
    }

    return results;
  },

  // ── draw ─────────────────────────────────────────────────────────────────
  draw: (params: unknown): boolean => {
    const {
      ctx, visibleRange, bounding, xAxis, yAxis, indicator, barSpace,
    } = params as DrawParams;

    const results = indicator.result;
    const { from, to } = visibleRange;
    if (to < from || !results.length) return true;

    const { gapBar, halfGapBar } = barSpace;
    const paneH = bounding.height;

    ctx.save();

    // ── Pass 1: background fills for in_trend bars ─────────────────────────
    ctx.fillStyle = FILL_COLOR;
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r?.in_trend) continue;
      const x = xAxis.convertToPixel(i);
      ctx.fillRect(x - halfGapBar, 0, gapBar + 0.5, paneH);
    }

    // ── Pass 2a: EMA21 polyline ────────────────────────────────────────────
    ctx.strokeStyle = EMA21_COLOR;
    ctx.lineWidth   = 1.5;
    ctx.setLineDash([]);
    ctx.beginPath();
    let first = true;
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r || r.ema21 === null) { first = true; continue; }
      const x = xAxis.convertToPixel(i);
      const y = yAxis.convertToPixel(r.ema21);
      if (first) { ctx.moveTo(x, y); first = false; } else { ctx.lineTo(x, y); }
    }
    ctx.stroke();

    // ── Pass 2b: SMA50 dashed polyline ────────────────────────────────────
    ctx.strokeStyle = SMA50_COLOR;
    ctx.setLineDash([6, 3]);
    ctx.beginPath();
    first = true;
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r || r.sma50 === null) { first = true; continue; }
      const x = xAxis.convertToPixel(i);
      const y = yAxis.convertToPixel(r.sma50);
      if (first) { ctx.moveTo(x, y); first = false; } else { ctx.lineTo(x, y); }
    }
    ctx.stroke();
    ctx.setLineDash([]);

    // ── Pass 3: event vertical lines ──────────────────────────────────────
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r) continue;
      const x = xAxis.convertToPixel(i);

      if (r.is_signal) {
        ctx.strokeStyle = SIGNAL_COLOR;
        ctx.lineWidth   = 2;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, paneH);
        ctx.stroke();
      }
      if (r.is_end_drawdown) {
        ctx.strokeStyle = END_DROP_COLOR;
        ctx.lineWidth   = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, paneH);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (r.is_end_cross) {
        ctx.strokeStyle = END_CROSS_COLOR;
        ctx.lineWidth   = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, paneH);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // ── Pass 4: current stop level line + label ───────────────────────────
    const lastResult = results[results.length - 1];
    if (lastResult?.in_trend && lastResult.stop_level !== null) {
      const stopY  = yAxis.convertToPixel(lastResult.stop_level);
      const leftX  = xAxis.convertToPixel(from);
      const rightX = xAxis.convertToPixel(to);

      ctx.strokeStyle = STOP_LINE_COLOR;
      ctx.lineWidth   = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(leftX, stopY);
      ctx.lineTo(rightX, stopY);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle    = STOP_LINE_COLOR;
      ctx.font         = 'bold 10px sans-serif';
      ctx.textAlign    = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`SL ${lastResult.stop_level.toFixed(2)}`, rightX - 2, stopY - 2);
    }

    // ── Pass 5: status badge (only when trend is active) ─────────────────
    const isActive = lastResult?.in_trend ?? false;
    if (isActive) {
      const rightX = xAxis.convertToPixel(to);
      ctx.fillStyle    = BADGE_ACTIVE;
      ctx.font         = 'bold 11px sans-serif';
      ctx.textAlign    = 'right';
      ctx.textBaseline = 'top';
      ctx.fillText('⬤ POWER TREND', rightX - 2, 8);
    }

    ctx.restore();
    return true; // suppress klinecharts default rendering
  },

  // ── tooltip ───────────────────────────────────────────────────────────────
  createTooltipDataSource: (params: unknown) => {
    const p = params as {
      indicator: { result: MomentumResult[] };
      crosshair: { dataIndex?: number };
    };
    const idx = p.crosshair.dataIndex ?? -1;
    const r = p.indicator.result[idx];
    if (!r) return { name: 'Momentum', calcParamsText: '', icons: [], values: [] };

    const values: { title: string; value: string }[] = [
      { title: 'C1 Low>EMA21: ', value: r.c1_met ? `✓ ${r.c1_streak}d` : `${r.c1_streak}/${C1_THRESHOLD}d` },
      { title: 'C2 EMA21>SMA50: ', value: r.c2_met ? `✓ ${r.c2_streak}d` : `${r.c2_streak}/${C2_THRESHOLD}d` },
      { title: 'C3 SMA50↑: ', value: r.c3_met ? '✓' : '—' },
      { title: 'C4 Green: ', value: r.c4_green ? '✓' : '—' },
    ];
    if (r.in_trend && r.stop_level !== null) {
      values.push({ title: 'Stop: ', value: r.stop_level.toFixed(2) });
    }

    return { name: 'Momentum', calcParamsText: '', icons: [], values };
  },
};

export default momentumOverlay;
